import { useState, useEffect } from 'react';
import { APP_NAME } from '@/lib/brand';
import { useParams } from 'react-router-dom';
import { Phone, Mail, MessageCircle, Home, Star, Linkedin, Instagram, Facebook, ChevronRight } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SocialIcon = ({ network, className }) => {
  const icons = {
    linkedin: <Linkedin className={className} />, 
    instagram: <Instagram className={className} />,
    facebook: <Facebook className={className} />,
    tiktok: <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.11-2.85-.56-4.06-1.35-.11-.07-.22-.15-.33-.23.01 2.18.01 4.36 0 6.54-.03 1.9-.45 3.8-1.57 5.31-1.29 1.79-3.4 2.82-5.58 2.8-2.63-.02-5.11-1.63-6.19-4.04-1.12-2.51-.41-5.63 1.73-7.39.95-.79 2.13-1.27 3.36-1.37.01 1.34.01 2.67.01 4.01-1.04.1-2.02.66-2.56 1.55-.78 1.25-.5 3.01.64 3.96.93.79 2.37.8 3.35.04 1-.78 1.34-2.14 1.3-3.39-.01-4.74-.01-9.48-.01-14.22z"/></svg>,
    threads: <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12.186 24h-.007C5.461 23.956.052 18.509 0 11.73v-.461C.052 5.058 5.461-.389 12.186-.433h.007c6.722.044 12.131 5.491 12.183 12.224v.461C24.324 18.983 18.915 24.43 12.193 24h-.007zm5.441-8.764c-.07-2.045-1.224-3.543-3.262-4.304a4.2 4.2 0 00-.466-.14c-.075-.019-.15-.036-.226-.051l.001-.005a6.49 6.49 0 00-.339-.06c-.19-.028-.38-.047-.571-.058-.1-.006-.2-.01-.3-.012h-.033c-.02 0-.04 0-.06.001-.34.006-.68.037-1.013.094a5.68 5.68 0 00-2.466 1.124 3.775 3.775 0 00-1.225 2.078c-.157.628-.16 1.289-.008 1.917.255 1.052.93 1.862 1.902 2.326.93.444 1.953.525 2.942.233.783-.232 1.399-.704 1.781-1.365.327-.567.453-1.227.364-1.903-.075-.573-.329-1.043-.756-1.396-.322-.266-.72-.382-1.134-.329-.268.034-.498.153-.664.343a.844.844 0 00-.15.737c.03.1.08.192.148.27a.622.622 0 00.393.22c.171.024.283.155.261.326a.288.288 0 01-.326.261c-.41-.058-.72-.298-.873-.677a1.36 1.36 0 01.04-1.063c.278-.576.85-.897 1.567-.88.604.015 1.144.253 1.518.668.482.535.694 1.283.599 2.11-.124 1.078-.683 1.924-1.614 2.45-1.29.73-2.795.773-4.122.117-1.22-.604-2.02-1.73-2.198-3.096-.178-1.359.148-2.638 1.04-3.605a5.18 5.18 0 013.67-1.717c.42-.022.841-.007 1.256.044a6.122 6.122 0 011.726.481c1.876.77 2.93 2.24 2.993 4.175.033 1.023-.205 1.985-.707 2.86-.615 1.073-1.545 1.758-2.763 2.035a5.023 5.023 0 01-1.206.126 4.61 4.61 0 01-1.737-.336c-.023-.009-.044-.02-.067-.03-.063.138-.136.27-.22.396-.525.793-1.34 1.199-2.289 1.149a2.45 2.45 0 01-1.644-.755 2.583 2.583 0 01-.671-1.887c.026-.757.322-1.42.857-1.916a2.55 2.55 0 011.813-.716c.397.01.773.11 1.11.296.032.017.063.036.094.055.011-.051.024-.102.038-.152z"/></svg>,
  };
  return icons[network] || null;
};

export default function VCardPublicPage() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/vcard/public/${slug}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(data => { setCard(data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  useEffect(() => {
    if (!card) return;
    document.title = `${card.full_name} — ${card.position_title || 'Tarjeta Virtual'}`;
    const setMeta = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`);
      if (!el) { el = document.createElement('meta'); prop.startsWith('og:') ? el.setAttribute('property', prop) : el.setAttribute('name', prop); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('og:title', `${card.full_name} — ${card.position_title}`);
    setMeta('og:description', (card.professional_bio || '').substring(0, 150));
    if (card.photo_url) setMeta('og:image', card.photo_url.startsWith('http') ? card.photo_url : `${API_BASE}${card.photo_url}`);
    setMeta('og:url', window.location.href);
    setMeta('twitter:card', 'summary_large_image');
  }, [card]);

  if (loading) return <div className="min-h-screen bg-[#0a0c12] flex items-center justify-center"><div className="text-white/40 text-sm">Cargando...</div></div>;
  if (notFound) return (
    <div className="min-h-screen bg-[#0a0c12] flex items-center justify-center" data-testid="vcard-404">
      <div className="text-center"><h1 className="text-3xl font-bold text-white mb-2">404</h1><p className="text-white/40">Tarjeta no encontrada</p></div>
    </div>
  );

  const photoUrl = card.photo_url ? (card.photo_url.startsWith('http') ? card.photo_url : `${API_BASE}${card.photo_url}`) : '';
  const coverUrl = card.cover_url ? (card.cover_url.startsWith('http') ? card.cover_url : `${API_BASE}${card.cover_url}`) : '';
  const activeSocials = Object.entries(card.social_links || {}).filter(([, v]) => v.enabled && v.url);
  const waLink = card.whatsapp ? `https://wa.me/${card.whatsapp.replace(/[^0-9+]/g, '')}` : '';

  return (
    <div className="min-h-screen bg-[#0a0c12] text-white selection:bg-[#A5D62C]/30" data-testid="vcard-public-page">
      <main className="pt-8 px-4 md:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#111318] rounded-[2rem] overflow-hidden shadow-2xl shadow-black/60 border border-white/5 relative">
            {/* Cover */}
            <div className="relative h-48 md:h-56 w-full bg-[#1a1d24] overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-[#A5D62C]/10 to-transparent z-10" />
              {coverUrl ? <img src={coverUrl} alt="Cover" className="w-full h-full object-cover opacity-40" /> : <div className="w-full h-full bg-gradient-to-br from-[#A5D62C]/5 to-[#1a1d24]" />}
            </div>

            <div className="px-6 md:px-8 pb-12 -mt-20 relative z-20">
              {/* Profile Header */}
              <div className="flex flex-col items-center text-center">
                <div className="relative inline-block">
                  <div className="absolute inset-0 rounded-full border-4 border-[#A5D62C]/30 scale-110" />
                  <div className="w-36 h-36 md:w-44 md:h-44 rounded-full border-4 border-[#111318] overflow-hidden shadow-2xl relative">
                    {photoUrl ? <img src={photoUrl} alt={card.full_name} className="w-full h-full object-cover" data-testid="vcard-public-photo" /> : <div className="w-full h-full bg-[#1a1d24] flex items-center justify-center text-4xl font-bold text-white/20">{card.full_name?.charAt(0)}</div>}
                  </div>
                  <div className="absolute bottom-3 right-3 w-5 h-5 bg-green-500 rounded-full border-4 border-[#111318] animate-pulse" />
                </div>

                <div className="mt-6">
                  <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="vcard-public-name">{card.full_name}</h1>
                  {card.position_title && <p className="text-[#A5D62C] font-semibold tracking-wide mt-2 text-base md:text-lg">{card.position_title}</p>}
                  {card.company_name && <p className="text-white/40 text-xs md:text-sm tracking-[0.3em] uppercase mt-1">{card.company_name}</p>}
                </div>

                {/* Contact info */}
                <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-white/80 text-sm">
                  {card.phone_mobile && <a href={`tel:${card.phone_mobile}`} className="hover:text-[#A5D62C] transition-colors">{card.phone_mobile}</a>}
                  {card.phone_mobile && card.email && <span className="hidden sm:block w-1.5 h-1.5 rounded-full bg-[#A5D62C]/30" />}
                  {card.email && <a href={`mailto:${card.email}`} className="hover:text-[#A5D62C] transition-colors">{card.email}</a>}
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-center gap-5 mt-8">
                  {card.phone_mobile && (
                    <a href={`tel:${card.phone_mobile}`} title="Llamar" className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1a1d24] border border-white/10 hover:bg-[#A5D62C] hover:text-black transition-all duration-300 shadow-xl group" data-testid="vcard-btn-call">
                      <Phone className="w-5 h-5 text-[#A5D62C] group-hover:text-black" />
                    </a>
                  )}
                  {waLink && (
                    <a href={waLink} target="_blank" rel="noopener noreferrer" title="WhatsApp" className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1a1d24] border border-white/10 hover:bg-[#25D366] hover:text-white transition-all duration-300 shadow-xl group" data-testid="vcard-btn-wa">
                      <MessageCircle className="w-5 h-5 text-[#25D366] group-hover:text-white" />
                    </a>
                  )}
                  {card.email && (
                    <a href={`mailto:${card.email}`} title="Correo" className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1a1d24] border border-white/10 hover:bg-[#A5D62C] hover:text-black transition-all duration-300 shadow-xl group" data-testid="vcard-btn-email">
                      <Mail className="w-5 h-5 text-[#A5D62C] group-hover:text-black" />
                    </a>
                  )}
                </div>

                {/* Social links */}
                {activeSocials.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-white/5 w-full">
                    <p className="text-xs font-bold text-white/40 uppercase tracking-[0.2em] mb-5">Sígueme en redes sociales</p>
                    <div className="flex flex-wrap justify-center gap-3">
                      {activeSocials.map(([key, val]) => (
                        <a key={key} href={val.url.startsWith('http') ? val.url : `https://${val.url}`} target="_blank" rel="noopener noreferrer" title={key} className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1a1d24] hover:bg-white/10 transition-colors border border-white/5" data-testid={`vcard-social-${key}`}>
                          <SocialIcon network={key} className="w-5 h-5" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Content body */}
              <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10">
                {/* Left: Bio & Certs */}
                <div className="md:col-span-7 space-y-8">
                  {card.professional_bio && (
                    <section>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-8 h-px bg-[#A5D62C]" />
                        <h2 className="text-base md:text-lg font-bold uppercase tracking-widest">Experiencia Profesional</h2>
                      </div>
                      <p className="text-white/60 leading-relaxed text-base" data-testid="vcard-public-bio">{card.professional_bio}</p>
                    </section>
                  )}
                  {card.certifications?.length > 0 && (
                    <section>
                      <div className="flex items-center gap-3 mb-5">
                        <span className="w-8 h-px bg-[#A5D62C]" />
                        <h2 className="text-base md:text-lg font-bold uppercase tracking-widest">Trayectoria y Certificaciones</h2>
                      </div>
                      <div className="space-y-3">
                        {card.certifications.map(c => (
                          <div key={c.id} className="flex items-center gap-4 bg-white/[0.03] p-4 rounded-2xl border border-white/5 hover:border-[#A5D62C]/20 transition-all" data-testid={`vcard-public-cert-${c.id}`}>
                            <Star className="w-5 h-5 text-amber-400 shrink-0" fill="currentColor" />
                            <div className="flex flex-col gap-1">
                              <span className="text-white/90 font-medium text-sm">{c.title}</span>
                              {c.year_start && <span className="inline-block px-3 py-0.5 font-extrabold bg-white/5 rounded-md text-[#A5D62C] text-xs w-fit">{c.year_start}{c.year_end ? ` - ${c.year_end}` : ''}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>

                {/* Right: Portfolio */}
                <div className="md:col-span-5 space-y-5">
                  <div className="md:sticky md:top-8">
                    <h2 className="text-xs font-bold text-white/40 uppercase tracking-[0.3em] mb-5 pl-1">Portafolio Inmobiliario</h2>
                    <div className="space-y-4">
                      {card.link_options?.crm_site_enabled && card.link_options?.catalog_url && (
                        <a href={card.link_options.catalog_url.startsWith('http') ? card.link_options.catalog_url : `https://${card.link_options.catalog_url}`} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 bg-[#1a1d24] p-5 rounded-2xl border border-white/5 hover:bg-white/[0.06] transition-all duration-300 shadow-xl" data-testid="vcard-link-catalog">
                          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#A5D62C]/10 text-[#A5D62C] group-hover:bg-[#A5D62C] group-hover:text-black transition-colors shrink-0">
                            <Home className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-bold text-sm">Mi inventario de propiedades</div>
                            <div className="text-white/40 text-xs mt-0.5">Ver catálogo exclusivo</div>
                          </div>
                        </a>
                      )}
                      {card.link_options?.external_site_enabled && card.link_options?.external_url && (
                        <a href={card.link_options.external_url.startsWith('http') ? card.link_options.external_url : `https://${card.link_options.external_url}`} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 bg-[#1a1d24] p-5 rounded-2xl border border-white/5 hover:bg-white/[0.06] transition-all duration-300 shadow-xl" data-testid="vcard-link-external">
                          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-blue-500/10 text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-colors shrink-0">
                            <Home className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-bold text-sm">Inventario de propiedades de la inmobiliaria</div>
                            <div className="text-white/40 text-xs mt-0.5">Visitar sitio</div>
                          </div>
                        </a>
                      )}
                    </div>
                    {/* CTA */}
                    <div className="mt-6 p-6 rounded-2xl bg-gradient-to-br from-[#1a1d24] to-[#111318] border border-white/5 text-center">
                      <div className="text-white font-bold text-sm mb-4">¿Deseas una asesoría privada?</div>
                      <a href={waLink || `mailto:${card.email}`} target="_blank" rel="noopener noreferrer" className="block w-full bg-white text-black font-extrabold py-3 rounded-xl hover:bg-[#A5D62C] transition-all active:scale-[0.98] shadow-lg text-sm text-center" data-testid="vcard-cta-contact">
                        CONTÁCTAME AHORA
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="text-center py-6 text-white/20 text-xs">
        Powered by <span className="text-[#A5D62C]/60 font-bold">{APP_NAME}</span>
      </footer>
    </div>
  );
}
